import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  Badge,
  Button,
  Card,
  CARD_SECTION_TITLE_CLASS,
  FILTER_SELECT_CLASS,
  TABLE_HEADER_CELL_CLASS,
  TextButton,
} from '@safetyhub/ui';
import {
  isManualResultStale,
  MANUAL_RESULT_STALE_DAYS,
  STORY_CHECK_ENVS,
  storiesApi,
  storySpecKeys,
  type StoryCheckEnv,
  type StoryCheckStatus,
  type StoryListItem,
} from '../lib/stories';
import RecentRunsPanel from './story-monitor/RecentRunsPanel';
import RunnerGuide from './story-monitor/RunnerGuide';
import StoryDetailModal from './story-monitor/StoryDetailModal';
import StoryStatusBadge from './story-monitor/StoryStatusBadge';
import StorySummaryPanel from './story-monitor/StorySummaryPanel';
import {
  countManualStale,
  countVerifyDeclared,
  describeStoryScope,
  ENV_TONE,
  formatRatio,
  formatRelative,
  specFilterOptions,
  statusLabel,
  storyIdLabel,
  summarizeBySpec,
} from './story-monitor/storyMonitorView';

/**
 * 유저 스토리 모니터 (설계 4단계).
 *
 * 설계 정본: `docs/decisions/2026-10-07-prod-story-verification-monitor.md` ·
 * 진행 상태: `docs/work-streams/active/story-verification-monitor.md`.
 *
 * ## 이 화면이 지키는 세 가지
 * 1. **「판정 없음」을 「통과」와 섞지 않는다** — 섞으면 아무것도 안 잰 것이 초록으로 보인다.
 *    그래서 상태 색·라벨이 갈려 있고, 건강도 분모(`통과+위반`) 밖의 셋(공허·실패·미판정)을
 *    **항상 함께** 싣는다.
 * 2. **못 재는 것을 비워서 보인다** — 숙성 분포·커버리지 두 비율은 `registry_summary`에 있고
 *    그 표를 읽는 엔드포인트가 없다. 관측으로 재파생하지 않고 ⚫ 미제공으로 드러낸다.
 * 3. **서버 실행 버튼을 두지 않는다** — 서버는 불변식을 돌리지 않고 저장만 한다. 「전체 실행」
 *    자리는 `RunnerGuide`(안내)다.
 *
 * ## ⚠️ 한 응답으로 접는다
 * 명세별 집계는 **받은 `stories` 배열을 그 자리에서** 접는다(`summarizeBySpec`). 명세별로 또
 * 질의하면 그 사이에 run이 돌아 **행마다 기준이 달라진다** — 「수에는 기준이 붙어야 한다」.
 * 그래서 한 번에 `PAGE_LIMIT`까지 받고, 서버의 `total`보다 적게 받았으면 **부분 집계임을 적는다**.
 *
 * ## ⚠️ 「전수가 아니다」는 **두 축**이고 둘 다 적어야 한다 (2026-10-13)
 * ① **500캡** — 받은 건수가 `total`보다 적다(`partial` 배너) ② **필터** — 서버가
 * `counts: buildCounts(filtered)`를 내므로 `counts`·`total` 자체가 부분집합의 집계다(`scope` 배너).
 * ②가 **없었고** 그래서 상태 = `PASS`를 고르면 「건강도 100% · 공허·실패·미판정 0」이 떠서 이 화면이
 * 막으려는 오독을 **자기가 만들었다.** 두 배너가 같은 모양인 것은 **같은 종류의 사실**이기 때문이다.
 * ⚠️ 큐 필터는 ②가 아니다 — 클라이언트에서 목록만 접고 `counts`는 건드리지 않는다.
 */

/** 서버 상한(`@Max(500)`)과 같은 값 — 넘기면 400이다. */
const PAGE_LIMIT = 500;

type QueueFilter = 'none' | 'unacknowledged' | 'manual-stale';

const STATUS_OPTIONS: StoryCheckStatus[] = ['PASS', 'VIOLATION', 'VACUOUS', 'ERROR'];

export default function StoryMonitor() {
  const [env, setEnv] = useState<StoryCheckEnv | ''>('');
  const [specKey, setSpecKey] = useState<string>('');
  const [status, setStatus] = useState<StoryCheckStatus | ''>('');
  const [includeRetired, setIncludeRetired] = useState(false);
  const [queueFilter, setQueueFilter] = useState<QueueFilter>('none');
  const [guideOpen, setGuideOpen] = useState(false);
  const [detailId, setDetailId] = useState<string | null>(null);

  // ⚠️ 질의 키에 서버로 보내는 필터만 담는다 — 큐 필터는 클라이언트 접기라 재질의 대상이 아니다
  const listQueryKey = useMemo(
    () => ['admin', 'stories', { env, specKey, status, includeRetired }] as const,
    [env, specKey, status, includeRetired],
  );

  const { data, isLoading, isError, error, refetch, isFetching } = useQuery({
    queryKey: listQueryKey,
    queryFn: () =>
      storiesApi.list({
        limit: PAGE_LIMIT,
        ...(env !== '' && { env }),
        ...(specKey !== '' && { specKey }),
        ...(status !== '' && { status }),
        ...(includeRetired && { includeRetired: true }),
      }),
  });

  const stories = data?.stories ?? [];
  const specSummaries = useMemo(() => summarizeBySpec(stories), [stories]);
  const manualStale = useMemo(() => countManualStale(stories), [stories]);
  const verifyDeclared = useMemo(() => countVerifyDeclared(stories), [stories]);

  /**
   * 요약·명세별 표의 수가 **무엇에 대한 수인가** — 서버가 `buildCounts(filtered)`를 내므로 필터가
   * 걸리면 전수가 아니다. 큐 필터는 **넣지 않는다**(클라이언트 접기라 `counts`에 영향이 0이다).
   */
  const scope = useMemo(
    () => describeStoryScope({ env, specKey, status, includeRetired }),
    [env, specKey, status, includeRetired],
  );

  /**
   * 명세 드롭다운 — **응답에서 파생시키지 않는다.** 그러면 명세 A를 고른 순간 목록이 A 하나로 줄어
   * B로 바로 갈 수 없다(필터가 자기 선택지를 먹는다). 레지스트리 키가 정적 전수이고, 응답에만 있는
   * 키(DB 미러가 코드보다 앞선 은퇴 명세)와 현재 선택을 합쳐 **선택된 값이 늘 옵션에 있게** 한다.
   * `storySpecKeys()`는 top-level 호출을 만들지 않으려고 함수다 — 여기(`useMemo`)가 그 호출 자리다.
   */
  const specOptions = useMemo(
    () =>
      specFilterOptions({
        registryKeys: storySpecKeys(),
        storyKeys: stories.map((story) => story.specKey),
        selected: specKey,
      }),
    [stories, specKey],
  );

  const visibleStories = useMemo(() => {
    if (queueFilter === 'unacknowledged') {
      return stories.filter((story) => story.violations.openUnacknowledged > 0);
    }
    if (queueFilter === 'manual-stale') {
      return stories.filter(isManualResultStale);
    }
    return stories;
  }, [stories, queueFilter]);

  /** 받은 건수가 서버의 전체보다 적으면 **집계가 부분**이다 — 그 사실을 숨기지 않는다 */
  const partial = data !== undefined && stories.length < data.total;

  if (isLoading) {
    return <div className="flex h-64 items-center justify-center text-ink-500">불러오는 중…</div>;
  }

  if (isError || !data) {
    return (
      <div className="flex h-64 flex-col items-center justify-center gap-4">
        <p className="text-rose-600">
          {error instanceof Error ? error.message : '데이터를 불러올 수 없습니다.'}
        </p>
        <Button variant="primary" size="sm" onClick={() => void refetch()}>
          다시 시도
        </Button>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-5">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-xl font-extrabold tracking-tight text-ink-900">유저 스토리 모니터</h1>
          <p className="mt-1 text-xs text-ink-500">
            상태가 증거다 — prod 상태로 유저 스토리의 기대를 계속 잰다.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <TextButton onClick={() => setGuideOpen((value) => !value)}>
            {guideOpen ? '실행 방법 닫기' : '실행 방법'}
          </TextButton>
          <Button variant="secondary" size="sm" onClick={() => void refetch()} disabled={isFetching}>
            {isFetching ? '갱신 중…' : '새로 고침'}
          </Button>
        </div>
      </div>

      {guideOpen && <RunnerGuide />}

      <StorySummaryPanel
        counts={data.counts}
        total={data.total}
        scope={scope}
        verifyDeclared={verifyDeclared}
        manualStale={manualStale}
      />

      {/* 숙성 분포·커버리지 두 비율의 소유자 — 목록과 같은 `env` 필터를 따른다(기준을 함께 본다). */}
      <RecentRunsPanel env={env} />

      {partial && (
        <p className="rounded-card-sm bg-amber-50 px-3 py-2 text-xs font-semibold text-amber-800">
          ⚠️ 전체 {data.total}건 중 {stories.length}건만 받았다 — 아래 <strong>명세별 집계는 그 {stories.length}건
          기준</strong>이고 전수가 아니다. 상단 요약은 <code>limit</code>에 <strong>깎이지 않는다</strong>
          (서버가 센 값이다) — 다만 그것도 <strong>필터 범위 전체</strong>의 집계이지 전수가 아니니, 그
          범위는 위 배너가 말한다. 필터로 범위를 좁혀 볼 것.
        </p>
      )}

      <Card padding="none">
        <div className="flex flex-wrap items-end gap-3 p-4">
          <div>
            <label className="mb-1 block text-[11px] font-bold uppercase tracking-wide text-ink-500" htmlFor="story-env">
              측정 환경
            </label>
            <select
              id="story-env"
              className={FILTER_SELECT_CLASS}
              value={env}
              onChange={(event) => setEnv(event.target.value as StoryCheckEnv | '')}
            >
              <option value="">전체 (env별 최신을 함께 접는다)</option>
              {/* ⚠️ 손으로 열거하지 않는다 — 소유자는 shared `STORY_CHECK_ENVS`이고, 러너가 세 번째
                  env를 쓰게 되면 **이 드롭다운이 자동으로 늘어난다**(tsc는 빠진 `<option>`을 못 본다). */}
              {STORY_CHECK_ENVS.map((option) => (
                <option key={option} value={option}>
                  {option}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="mb-1 block text-[11px] font-bold uppercase tracking-wide text-ink-500" htmlFor="story-spec">
              명세
            </label>
            <select
              id="story-spec"
              className={FILTER_SELECT_CLASS}
              value={specKey}
              onChange={(event) => setSpecKey(event.target.value)}
            >
              <option value="">전체</option>
              {/* ⚠️ `specSummaries`(= 응답 파생)로 그리지 않는다 — 그러면 한 명세를 고른 뒤 그
                  하나만 남아 **다른 명세로 바로 갈 수 없다**(`specFilterOptions` 주석). */}
              {specOptions.map((option) => (
                <option key={option.specKey} value={option.specKey}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="mb-1 block text-[11px] font-bold uppercase tracking-wide text-ink-500" htmlFor="story-status">
              상태
            </label>
            <select
              id="story-status"
              className={FILTER_SELECT_CLASS}
              value={status}
              onChange={(event) => setStatus(event.target.value as StoryCheckStatus | '')}
            >
              <option value="">전체</option>
              {STATUS_OPTIONS.map((option) => (
                <option key={option} value={option}>
                  {statusLabel(option)}
                </option>
              ))}
            </select>
            {/* 「판정 없음」은 서버 필터로 고를 수 없다 — 파생 상태 필터가 enum에만 걸린다 */}
            <p className="mt-1 text-[10.5px] text-ink-400">「판정 없음」은 이 필터로 고를 수 없다</p>
          </div>
          <div className="flex flex-col gap-1">
            <span className="text-[11px] font-bold uppercase tracking-wide text-ink-500">큐</span>
            <div className="flex gap-1">
              <Button
                variant={queueFilter === 'unacknowledged' ? 'primary' : 'secondary'}
                size="sm"
                onClick={() =>
                  setQueueFilter((value) => (value === 'unacknowledged' ? 'none' : 'unacknowledged'))
                }
              >
                🔴 미확인 위반 {data.counts.violations.openUnacknowledged}
              </Button>
              <Button
                variant={queueFilter === 'manual-stale' ? 'primary' : 'secondary'}
                size="sm"
                onClick={() =>
                  setQueueFilter((value) => (value === 'manual-stale' ? 'none' : 'manual-stale'))
                }
              >
                ⚠️ 낡은 사람 판정 {manualStale}
              </Button>
            </div>
          </div>
          <label className="flex items-center gap-1.5 text-xs font-semibold text-ink-600">
            <input
              type="checkbox"
              checked={includeRetired}
              onChange={(event) => setIncludeRetired(event.target.checked)}
            />
            은퇴 스토리 포함
          </label>
        </div>
      </Card>

      <Card padding="none">
        <div className="flex items-baseline justify-between gap-3 px-4 pt-4">
          <h2 className={CARD_SECTION_TITLE_CLASS}>
            명세별
            {/* 같은 `scope`에서 파생 — 「스토리」 칸은 **받은 범위**의 수다(`SpecSummary.total` 주석) */}
            {scope.subset && (
              <span className="ml-2 text-[11px] font-semibold text-amber-700">
                필터 범위 — 「스토리」 칸은 그 명세의 전수가 아니다
              </span>
            )}
          </h2>
          <span className="text-[11px] text-ink-500">
            「건강도」 분모는 통과+위반뿐이다 — 공허·실패·미판정은 재지 못한 것이다
          </span>
        </div>
        <div className="mt-3 overflow-x-auto">
          <table className="min-w-full text-[12.5px]">
            <thead>
              <tr>
                <th className={TABLE_HEADER_CELL_CLASS}>명세</th>
                <th className={TABLE_HEADER_CELL_CLASS}>스토리</th>
                <th className={TABLE_HEADER_CELL_CLASS}>건강도</th>
                <th className={TABLE_HEADER_CELL_CLASS}>분모 밖</th>
                <th className={TABLE_HEADER_CELL_CLASS}>verify 선언</th>
                <th className={TABLE_HEADER_CELL_CLASS}>위반</th>
                <th className={TABLE_HEADER_CELL_CLASS}>낡은 사람 판정</th>
              </tr>
            </thead>
            <tbody>
              {specSummaries.map((summary) => (
                <tr key={summary.specKey} className="border-b border-line">
                  <td className="px-4 py-2 font-bold text-ink-900">{summary.label}</td>
                  <td className="px-4 py-2 tabular-nums">{summary.total}</td>
                  <td className="px-4 py-2 tabular-nums">
                    {formatRatio(summary.healthRatio)}{' '}
                    <span className="text-ink-500">
                      ({summary.pass}/{summary.healthDenominator})
                    </span>
                  </td>
                  <td className="px-4 py-2 text-ink-600">
                    공허 {summary.vacuous} · 실패 {summary.error} · 미판정 {summary.unjudged}
                  </td>
                  <td className="px-4 py-2 tabular-nums">{summary.verifyDeclared}</td>
                  <td className="px-4 py-2 tabular-nums">
                    {summary.openViolations > 0 ? (
                      <span className="font-bold text-rose-600">
                        🔴 {summary.openViolations}
                        {summary.openUnacknowledged !== summary.openViolations && (
                          <span className="ml-1 font-semibold text-ink-500">
                            (미확인 {summary.openUnacknowledged})
                          </span>
                        )}
                      </span>
                    ) : (
                      0
                    )}
                  </td>
                  <td className="px-4 py-2 tabular-nums">
                    {summary.manualStale > 0 ? (
                      <span className="font-bold text-amber-700">⚠️ {summary.manualStale}</span>
                    ) : (
                      0
                    )}
                  </td>
                </tr>
              ))}
              {specSummaries.length === 0 && (
                <tr>
                  <td className="px-4 py-6 text-center text-ink-500" colSpan={7}>
                    해당하는 스토리가 없습니다.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </Card>

      <Card padding="none">
        <div className="flex items-baseline justify-between gap-3 px-4 pt-4">
          <h2 className={CARD_SECTION_TITLE_CLASS}>스토리 {visibleStories.length}건</h2>
          {queueFilter !== 'none' && (
            <TextButton onClick={() => setQueueFilter('none')}>큐 필터 해제</TextButton>
          )}
        </div>
        <div className="mt-3 overflow-x-auto">
          <table className="min-w-full text-[12.5px]">
            <thead>
              <tr>
                <th className={TABLE_HEADER_CELL_CLASS}>스토리</th>
                <th className={TABLE_HEADER_CELL_CLASS}>기대</th>
                <th className={TABLE_HEADER_CELL_CLASS}>상태</th>
                <th className={TABLE_HEADER_CELL_CLASS}>판정</th>
                <th className={TABLE_HEADER_CELL_CLASS}>위반</th>
              </tr>
            </thead>
            <tbody>
              {visibleStories.map((story) => (
                <StoryRow key={story.id} story={story} onOpen={() => setDetailId(story.id)} />
              ))}
              {visibleStories.length === 0 && (
                <tr>
                  <td className="px-4 py-6 text-center text-ink-500" colSpan={5}>
                    해당하는 스토리가 없습니다.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </Card>

      {detailId !== null && (
        <StoryDetailModal
          storyId={detailId}
          open
          onClose={() => setDetailId(null)}
          listQueryKey={listQueryKey}
        />
      )}
    </div>
  );
}

/** 목록 한 줄. `US-0013 (사진 P13)` 둘 다 — 기존 보고서·저널의 「P13」을 추적할 수 있어야 한다. */
function StoryRow({ story, onOpen }: { story: StoryListItem; onOpen: () => void }) {
  const stale = isManualResultStale(story);
  return (
    <tr className="border-b border-line align-top hover:bg-surface-hover">
      <td className="px-4 py-2">
        <button type="button" onClick={onOpen} className="text-left">
          <span className="font-bold text-brand-700 hover:underline">{storyIdLabel(story)}</span>
        </button>
        <div className="mt-0.5 text-ink-900">{story.title}</div>
        {story.retiredAt && (
          <span className="mt-0.5 inline-flex align-middle">
            <Badge tone="slate" size="sm">
              은퇴
            </Badge>
          </span>
        )}
      </td>
      <td className="max-w-[28rem] px-4 py-2 text-ink-600">{story.expectation}</td>
      <td className="px-4 py-2">
        <div className="flex flex-col items-start gap-1">
          <StoryStatusBadge status={story.status} size="sm" />
          {story.measuredEnvs.length > 0 && (
            <div className="flex gap-1">
              {story.measuredEnvs.map((measuredEnv) => (
                <span key={measuredEnv} className="inline-flex align-middle">
                  <Badge tone={ENV_TONE[measuredEnv] ?? 'slate'} size="sm">
                    {measuredEnv}
                  </Badge>
                </span>
              ))}
            </div>
          )}
        </div>
      </td>
      <td className="px-4 py-2 text-ink-600">
        <div>{formatRelative(story.latestCheckedAt)}</div>
        <div className="text-[11px] text-ink-500">
          {story.resultCount}칸
          {story.hasManualResult && ' · 사람 판정 섞임'}
          {story.verifyKind && ` · verify: ${story.verifyKind}`}
        </div>
        {stale && (
          <div className="text-[11px] font-semibold text-amber-700">
            ⚠️ {MANUAL_RESULT_STALE_DAYS}일+ 지난 사람 판정
          </div>
        )}
      </td>
      <td className="px-4 py-2 tabular-nums">
        {story.violations.open > 0 ? (
          <div className="flex flex-col">
            <span className="font-bold text-rose-600">🔴 {story.violations.open}</span>
            <span className="text-[11px] text-ink-500">
              미확인 {story.violations.openUnacknowledged} · 확인{' '}
              {story.violations.openAcknowledged}
            </span>
          </div>
        ) : (
          <span className="text-ink-500">0</span>
        )}
        {story.violations.resolved > 0 && (
          <span className="text-[11px] text-emerald-700">해소 {story.violations.resolved}</span>
        )}
      </td>
    </tr>
  );
}
